/* Master Order → Brand Studio — Sprint Brand-Studio-MVP (Demo flow).
 *
 * A single, complete user flow the confectioner can walk through end to
 * end for the demo video:
 *   1. Land on Brand Studio preview page.
 *   2. Tap «Создать мой бренд» → 5-step wizard (name/mood/palette/shape/contacts).
 *   3. Tap «Создать варианты» → pick one of 4 deterministically-generated logos.
 *   4. «Сохранить мой бренд» → persistent Brand Profile screen.
 *   5. See «Ваш бренд в работе» — Наклейка/Визитка/Карточка хранения previews
 *      auto-generated from the profile.
 *
 * NO real AI generation. Everything is SVG rendered deterministically from
 * the user's answers so the demo feels alive without paid infrastructure.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Palette, FileImage, Ticket, Sticker, Ribbon, Refrigerator,
  Truck, Scissors, Heart, Bookmark, Send, Layers, Layout, Paperclip,
  Stamp, Check, Edit2, ChevronLeft, ChevronRight, X, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import MOShell from "@/components/masterOrder/MOShell";
import { getBrandProfile, saveBrandProfile } from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

/* ─────────────────────────── static config ─────────────────────────── */

const PLANNED = [
  { id: "logos",       label: "Логотипы",              Icon: FileImage },
  { id: "cards",       label: "Визитки",               Icon: Ticket },
  { id: "tags",        label: "Бирки",                 Icon: Bookmark },
  { id: "stickers",    label: "Наклейки",              Icon: Sticker },
  { id: "ribbons",     label: "Фирменные ленты",       Icon: Ribbon },
  { id: "storage",     label: "Гид по хранению и подаче", Icon: Refrigerator },
  { id: "transport",   label: "Карточки транспортировки", Icon: Truck },
  { id: "cutting",     label: "Инструкции по нарезке", Icon: Scissors },
  { id: "gratitude",   label: "Благодарственные карточки", Icon: Heart },
  { id: "references",  label: "Референсы",             Icon: Layers },
  { id: "templates",   label: "Шаблоны для клиента",   Icon: Send },
  { id: "stamps",      label: "Штампы",                Icon: Stamp },
];

const STRUCTURE = [
  { id: "materials", label: "Мои материалы",     Icon: FileImage,
    hint: "Личная библиотека фирменных файлов." },
  { id: "templates", label: "Шаблоны",           Icon: Layout,
    hint: "Готовые заготовки на основе Ваших материалов." },
  { id: "attach",    label: "Прикрепить к заказу", Icon: Paperclip,
    hint: "Собрать нужные материалы под конкретный заказ." },
];

const MOODS = [
  { id: "premium",  label: "Премиальный",   accent: "#7A6247", serifTitle: true },
  { id: "minimal",  label: "Минималистичный", accent: "#2A1D14", serifTitle: false },
  { id: "gentle",   label: "Нежный",         accent: "#C68A93", serifTitle: true },
  { id: "classic",  label: "Классический",   accent: "#4A3728", serifTitle: true },
  { id: "modern",   label: "Современный",    accent: "#3D5A6C", serifTitle: false },
];

const PALETTES = [
  { id: "sand",    name: "Тёплый песок",      colors: ["#F7EFE0", "#EADFCF", "#C7A574", "#4A3728"] },
  { id: "cream",   name: "Кремовый румянец",  colors: ["#FBF5EA", "#F3DAD4", "#C68A93", "#6C4A50"] },
  { id: "sage",    name: "Пыльный шалфей",    colors: ["#F1EEE6", "#D8DCC6", "#8FA383", "#3E5344"] },
  { id: "cocoa",   name: "Тёмный какао",      colors: ["#F5EBD8", "#C9AC85", "#6E4B2E", "#231710"] },
  { id: "sky",     name: "Утреннее небо",     colors: ["#F4F1EC", "#DCE6EC", "#96B0BE", "#3D5A6C"] },
];

const LOGO_SHAPES = [
  { id: "monogram",    label: "Монограмма" },
  { id: "round",       label: "Круглый" },
  { id: "square",      label: "Квадратный" },
  { id: "horizontal",  label: "Горизонтальный" },
];

/* ─────────────────────────── page shell ─────────────────────────── */

export default function MOBrandStudio() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const p = await getBrandProfile();
        setProfile(p || null);
      } catch { /* silent — 404 for new users */ }
      finally { setLoading(false); }
    })();
  }, []);

  const onWizardSaved = (bp) => {
    setProfile(bp);
    setWizardOpen(false);
    toast.success("Ваш бренд сохранён");
  };

  return (
    <MOShell active="brand-studio" title="Brand Studio"
             subtitle={profile ? `Ваш бренд: ${profile.brand_name}` : "Пространство для оформления заказов — демонстрация."}>
      <div data-testid="mo-brand-studio"
           className="pb-16 max-w-4xl overflow-x-clip">

        <div className="flex items-center gap-2 mb-4">
          <button onClick={() => nav("/master-order")}
                  className="h-9 px-3 rounded-full text-[12.5px] inline-flex items-center gap-1"
                  style={{ background: "#F7F1E7", color: "#4A3728",
                           boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  data-testid="mo-brand-studio-back">
            <ArrowLeft size={13} /> В «Мастер заказа»
          </button>
        </div>

        {/* Hero */}
        <section className="rounded-2xl p-5 md:p-7 mb-4"
                 style={{
                   background: "linear-gradient(135deg, #F3E7CE 0%, #EADFCF 60%, #E1CFAA 100%)",
                   boxShadow: "0 1px 0 rgba(74,55,40,0.06), 0 18px 32px -24px rgba(74,55,40,0.22)",
                 }}
                 data-testid="mo-brand-studio-hero">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full grid place-items-center shrink-0"
                 style={{ background: "#4A3728", color: "#F7EFE0" }}>
              <Palette size={19} strokeWidth={1.6} />
            </div>
            <div className="min-w-0">
              <div className="text-[10.5px] tracking-[0.3em]" style={{ color: "#8B6F4B" }}>ANOKINA STUDIO</div>
              <h1 className="mt-1 text-[26px] md:text-[32px] leading-tight"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
                  data-testid="mo-brand-studio-title">
                Brand Studio
              </h1>
              <div className="mt-1.5">
                <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.14em] uppercase px-2.5 py-1 rounded-full"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-brand-studio-status">
                  В разработке
                </span>
              </div>
            </div>
          </div>
          <p className="mt-4 text-[13.5px] md:text-[14px] leading-relaxed max-w-2xl"
             style={{ color: "#3D2C1E" }}
             data-testid="mo-brand-studio-intro">
            Пространство для оформления заказов и общения с клиентами.
            Здесь Вы сможете хранить фирменные материалы, использовать
            готовые шаблоны и собирать материалы для конкретного заказа.
          </p>

          {/* Primary CTA */}
          <div className="mt-5">
            {loading ? (
              <div className="text-[12.5px]" style={{ color: "#8B6F4B" }}>Загружаем…</div>
            ) : profile ? (
              <button onClick={() => setWizardOpen(true)}
                      className="h-11 px-5 rounded-full text-[13.5px] inline-flex items-center gap-2"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-brand-studio-open-my-brand">
                <Palette size={14} /> Мой бренд
              </button>
            ) : (
              <button onClick={() => setWizardOpen(true)}
                      className="h-11 px-5 rounded-full text-[13.5px] inline-flex items-center gap-2"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-brand-studio-create-my-brand">
                <Palette size={14} /> Создать мой бренд
              </button>
            )}
          </div>
        </section>

        {/* Live brand preview when profile exists */}
        {profile && (
          <>
            <BrandProfileView profile={profile} onEdit={() => setWizardOpen(true)} />
            <BrandInAction profile={profile} />
          </>
        )}

        {/* Планируемые материалы */}
        <section className="mb-4" data-testid="mo-brand-studio-planned">
          <div className="text-[10.5px] tracking-[0.18em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
            Что планируется
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {PLANNED.map(({ id, label, Icon }) => (
              <div key={id}
                   className="rounded-xl bg-white p-3 flex items-center gap-2.5"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                   data-testid={`mo-brand-studio-plan-${id}`}>
                <div className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                     style={{ background: "#F5EBD8", color: "#8B6F4B" }}>
                  <Icon size={14} strokeWidth={1.7} />
                </div>
                <div className="text-[12.5px] leading-tight" style={{ color: "#2A1D14" }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Будущая структура */}
        <section className="rounded-2xl bg-white p-4 md:p-5"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                 data-testid="mo-brand-studio-structure">
          <div className="text-[10.5px] tracking-[0.18em] uppercase mb-3" style={{ color: "#8B6F4B" }}>
            Будущая структура Brand Studio
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
            {STRUCTURE.map(({ id, label, Icon, hint }) => (
              <div key={id}
                   className="rounded-xl p-3.5"
                   style={{
                     background: "#FBF5EA",
                     boxShadow: "inset 0 0 0 1px #EFE4D0",
                     opacity: 0.85,
                   }}
                   data-testid={`mo-brand-studio-struct-${id}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                       style={{ background: "#EADFCF", color: "#8B6F4B" }}>
                    <Icon size={14} strokeWidth={1.7} />
                  </div>
                  <div className="text-[13.5px]" style={{ color: "#2A1D14" }}>{label}</div>
                </div>
                <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>{hint}</div>
                <div className="mt-2 inline-flex items-center gap-1 text-[10.5px] tracking-[0.14em] uppercase px-2 py-0.5 rounded-full"
                     style={{ background: "#F5EBD8", color: "#8B6F4B",
                              boxShadow: "inset 0 0 0 1px #EADFCF" }}>
                  скоро
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {wizardOpen && (
        <BrandWizard profile={profile}
                     onClose={() => setWizardOpen(false)}
                     onSaved={onWizardSaved} />
      )}
    </MOShell>
  );
}

/* ─────────────────────────── logo mocks ───────────────────────────
 * Deterministic SVG mocks so 4 variants feel like directions of one brand.
 * They read from the same brand_name + monogram + mood + palette. */

function LogoSVG({ variant, profile, size = 220 }) {
  const { brand_name, monogram, palette } = profile;
  const colors = palette?.colors || ["#F7EFE0", "#EADFCF", "#C7A574", "#4A3728"];
  const mood = MOODS.find((m) => m.id === profile.mood) || MOODS[0];
  const serifFamily = "'Cormorant Garamond', serif";
  const sansFamily = "'Inter', sans-serif";
  const family = mood.serifTitle ? serifFamily : sansFamily;
  const ink = colors[colors.length - 1];
  const soft = colors[0];
  const accent = colors[Math.max(0, colors.length - 2)];
  const w = size, h = size;
  const initials = (monogram || (brand_name || "A").trim().split(/\s+/).map(w => w[0]).join("").slice(0, 3)).toUpperCase();

  if (variant === "typographic") {
    return (
      <svg width="100%" viewBox={`0 0 400 240`} preserveAspectRatio="xMidYMid meet">
        <rect width="400" height="240" fill={soft} rx="8" />
        <text x="200" y="120" fontFamily={family} fontSize="40" letterSpacing="8" fill={ink}
              textAnchor="middle" dominantBaseline="middle">{brand_name || "Ваш бренд"}</text>
        <line x1="120" y1="150" x2="280" y2="150" stroke={accent} strokeWidth="1.4" />
        <text x="200" y="180" fontFamily={sansFamily} fontSize="10" letterSpacing="6" fill={accent}
              textAnchor="middle">{mood.label.toUpperCase()}</text>
      </svg>
    );
  }
  if (variant === "monogram") {
    return (
      <svg width="100%" viewBox={`0 0 240 240`} preserveAspectRatio="xMidYMid meet">
        <rect width="240" height="240" fill={soft} rx="8" />
        <circle cx="120" cy="115" r="72" fill="none" stroke={accent} strokeWidth="2" />
        <text x="120" y="128" fontFamily={family} fontSize="72" fontWeight="500" fill={ink}
              textAnchor="middle" dominantBaseline="middle">{initials || "A"}</text>
        <text x="120" y="205" fontFamily={sansFamily} fontSize="8" letterSpacing="6" fill={accent}
              textAnchor="middle">{(brand_name || "").toUpperCase().slice(0,24)}</text>
      </svg>
    );
  }
  if (variant === "mark_and_name") {
    return (
      <svg width="100%" viewBox={`0 0 400 200`} preserveAspectRatio="xMidYMid meet">
        <rect width="400" height="200" fill={soft} rx="8" />
        {/* petite mark */}
        <g transform="translate(80,100)">
          <circle r="40" fill={accent} />
          <text x="0" y="4" fontFamily={family} fontSize="30" fill={soft}
                textAnchor="middle" dominantBaseline="middle">{initials.slice(0,2)}</text>
        </g>
        <text x="150" y="95" fontFamily={family} fontSize="28" fill={ink}
              dominantBaseline="middle">{brand_name || "Ваш бренд"}</text>
        <text x="150" y="125" fontFamily={sansFamily} fontSize="9" letterSpacing="4" fill={accent}
              dominantBaseline="middle">{mood.label.toUpperCase()}</text>
      </svg>
    );
  }
  // compact — for sticker/stamp
  return (
    <svg width="100%" viewBox={`0 0 200 200`} preserveAspectRatio="xMidYMid meet">
      <rect width="200" height="200" fill={soft} rx="8" />
      <circle cx="100" cy="100" r="80" fill="none" stroke={ink} strokeWidth="2" />
      <circle cx="100" cy="100" r="72" fill="none" stroke={accent} strokeWidth="0.7" strokeDasharray="2 3" />
      <text x="100" y="108" fontFamily={family} fontSize="46" fontWeight="500" fill={ink}
            textAnchor="middle" dominantBaseline="middle">{initials.slice(0,2)}</text>
    </svg>
  );
}

const LOGO_VARIANTS = [
  { id: "typographic",   label: "Типографический" },
  { id: "monogram",      label: "Монограмма" },
  { id: "mark_and_name", label: "Знак + название" },
  { id: "compact",       label: "Компактная (для наклейки)" },
];

/* ─────────────────────────── Brand profile view ─────────────────────────── */

function BrandProfileView({ profile, onEdit }) {
  const mood = MOODS.find((m) => m.id === profile.mood);
  const shape = LOGO_SHAPES.find((s) => s.id === profile.logo_shape);
  return (
    <section className="rounded-2xl bg-white p-5 md:p-6 mb-4"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
             data-testid="mo-brand-studio-profile">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10.5px] tracking-[0.28em] uppercase" style={{ color: "#8B6F4B" }}>МОЙ БРЕНД</div>
          <div className="mt-1 text-[24px]"
               style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
               data-testid="mo-brand-studio-profile-name">
            {profile.brand_name}
          </div>
        </div>
        <button onClick={onEdit}
                className="h-9 px-3 rounded-full text-[12px] inline-flex items-center gap-1.5"
                style={{ background: "#FBF5EA", color: "#4A3728",
                         boxShadow: "inset 0 0 0 1px #D9C6A6" }}
                data-testid="mo-brand-studio-profile-edit">
          <Edit2 size={12} /> Редактировать бренд
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,280px)_1fr] gap-4">
        <div className="rounded-xl overflow-hidden"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
          <LogoSVG variant={profile.logo_variant?.id || "monogram"} profile={profile} />
        </div>
        <div className="grid grid-cols-2 gap-3 text-[12.5px]" style={{ color: "#5B4326" }}>
          <ProfileRow label="Монограмма" value={profile.monogram || "—"} />
          <ProfileRow label="Направление" value={mood?.label || "—"} />
          <ProfileRow label="Форма логотипа" value={shape?.label || "—"} />
          <ProfileRow label="Палитра" value={
            <div className="flex items-center gap-1.5">
              {(profile.palette?.colors || []).map((c) => (
                <span key={c} className="w-4 h-4 rounded-full"
                      style={{ background: c, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }} />
              ))}
              <span className="ml-1 text-[11.5px]" style={{ color: "#8B6F4B" }}>{profile.palette?.name}</span>
            </div>
          } />
          <ProfileRow label="Instagram" value={profile.contacts?.instagram || "—"} />
          <ProfileRow label="Телефон" value={profile.contacts?.phone || "—"} />
          <ProfileRow label="Сайт" value={profile.contacts?.website || "—"} full />
        </div>
      </div>
    </section>
  );
}

function ProfileRow({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] tracking-[0.2em] uppercase mb-0.5" style={{ color: "#8B6F4B" }}>{label}</div>
      <div style={{ color: "#2A1D14" }}>{value || <span style={{ color: "#B9A692" }}>—</span>}</div>
    </div>
  );
}

/* ─────────────────────────── Brand-in-action mockups ─────────────────────────── */

function BrandInAction({ profile }) {
  const c = profile.palette?.colors || ["#F7EFE0", "#EADFCF", "#C7A574", "#4A3728"];
  const soft = c[0], mid = c[1] || c[0], accent = c[Math.max(0, c.length-2)], ink = c[c.length-1];
  return (
    <section className="mb-6" data-testid="mo-brand-studio-in-action">
      <div className="text-[10.5px] tracking-[0.18em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
        Ваш бренд в работе
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Sticker */}
        <div className="rounded-2xl p-3 grid place-items-center"
             style={{ background: mid, boxShadow: "inset 0 0 0 1px #EFE4D0", minHeight: 200 }}
             data-testid="mo-brand-studio-mock-sticker">
          <div className="rounded-full bg-[color:var(--soft)] w-40 h-40 grid place-items-center relative"
               style={{ background: soft, boxShadow: `inset 0 0 0 2px ${ink}` }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", color: ink, textAlign: "center" }}>
              <div className="text-[10px] tracking-[0.28em] uppercase mb-1" style={{ color: accent }}>Handmade</div>
              <div className="text-[20px] leading-tight">{profile.brand_name}</div>
              {profile.monogram && <div className="mt-1 text-[10px]" style={{ color: accent }}>{profile.monogram}</div>}
            </div>
          </div>
          <div className="mt-2 text-[10.5px] tracking-[0.16em] uppercase" style={{ color: ink }}>Наклейка</div>
        </div>
        {/* Business card */}
        <div className="rounded-2xl p-3 grid place-items-center"
             style={{ background: soft, boxShadow: "inset 0 0 0 1px #EFE4D0", minHeight: 200 }}
             data-testid="mo-brand-studio-mock-card">
          <div className="rounded-md w-full max-w-[260px] aspect-[1.7/1] px-4 py-3 flex flex-col justify-between"
               style={{ background: "#FFFFFF", boxShadow: `inset 0 0 0 1px ${accent}` }}>
            <div>
              <div className="text-[9px] tracking-[0.24em] uppercase" style={{ color: accent }}>ANOKINA</div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", color: ink, fontSize: 20, lineHeight: 1.1, marginTop: 2 }}>
                {profile.brand_name}
              </div>
            </div>
            <div className="text-[9.5px] leading-snug" style={{ color: ink }}>
              {profile.contacts?.instagram && <div>{profile.contacts.instagram}</div>}
              {profile.contacts?.phone && <div>{profile.contacts.phone}</div>}
              {profile.contacts?.website && <div>{profile.contacts.website}</div>}
            </div>
          </div>
          <div className="mt-2 text-[10.5px] tracking-[0.16em] uppercase" style={{ color: ink }}>Визитка</div>
        </div>
        {/* Storage & serving guide (compact, brand-driven, contacts optional) */}
        <div className="rounded-2xl p-3 grid place-items-center"
             style={{ background: mid, boxShadow: "inset 0 0 0 1px #EFE4D0", minHeight: 200 }}
             data-testid="mo-brand-studio-mock-storage">
          <div className="rounded-md w-full max-w-[260px] p-3.5 flex flex-col gap-2.5"
               style={{ background: soft, boxShadow: `inset 0 0 0 1px ${accent}` }}>
            <div>
              <div className="text-[9px] tracking-[0.24em] uppercase truncate"
                   style={{ color: accent }}
                   data-testid="mo-brand-studio-mock-storage-brand">
                {profile.brand_name}
              </div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", color: ink,
                            fontSize: 15, lineHeight: 1.1, marginTop: 3 }}>
                Гид по хранению и подаче
              </div>
              <div className="mt-1.5 h-px" style={{ background: accent, opacity: 0.5 }} />
            </div>

            <ul className="text-[10px] leading-snug space-y-1" style={{ color: ink }}
                data-testid="mo-brand-studio-mock-storage-tips">
              <li><span style={{ color: accent }}>·</span> Хранить при +2…+6 °C.</li>
              <li><span style={{ color: accent }}>·</span> Перевозить на ровной поверхности.</li>
              <li><span style={{ color: accent }}>·</span> Достать за 20–30 мин до подачи.</li>
              <li><span style={{ color: accent }}>·</span> Резать длинным сухим ножом.</li>
              <li><span style={{ color: accent }}>·</span> Свежий декор — перед подачей.</li>
            </ul>

            {(profile.contacts?.instagram || profile.contacts?.website || profile.contacts?.phone) && (
              <div className="pt-1.5 mt-0.5 text-[9px] leading-snug"
                   style={{ color: ink, borderTop: `1px solid ${accent}55` }}
                   data-testid="mo-brand-studio-mock-storage-contacts">
                {profile.contacts?.instagram && <div className="truncate">{profile.contacts.instagram}</div>}
                {profile.contacts?.website && <div className="truncate">{profile.contacts.website}</div>}
                {profile.contacts?.phone && <div className="truncate">{profile.contacts.phone}</div>}
              </div>
            )}
          </div>
          <div className="mt-2 text-[10.5px] tracking-[0.16em] uppercase" style={{ color: ink }}>Гид по хранению и подаче</div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── Wizard ─────────────────────────── */

/* Wizard step frame — extracted OUTSIDE the wizard component. Previously
 * it was declared inline as `const StepShell = ({...}) => (...)` inside
 * BrandWizard, which meant every keystroke (state update → re-render)
 * produced a NEW component identity. React then unmounted + remounted the
 * subtree — inputs lost focus after every character. Extracting fixes it. */
function WizardStep({ step, title, children }) {
  return (
    <div>
      <div className="text-[10.5px] tracking-[0.24em] uppercase mb-1" style={{ color: "#8B6F4B" }}>
        Шаг {step} из 6
      </div>
      <h3 className="text-[22px] leading-tight mb-4"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function BrandWizard({ profile, onClose, onSaved }) {
  const isEdit = !!profile;
  const [step, setStep] = useState(1); // 1..5, 6=variants
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => ({
    brand_name: profile?.brand_name || "",
    monogram: profile?.monogram || "",
    mood: profile?.mood || MOODS[0].id,
    palette: profile?.palette || PALETTES[0],
    logo_shape: profile?.logo_shape || LOGO_SHAPES[0].id,
    contacts: {
      instagram: profile?.contacts?.instagram || "",
      phone: profile?.contacts?.phone || "",
      website: profile?.contacts?.website || "",
    },
    logo_variant: profile?.logo_variant || null,
  }));
  const [customPaletteOpen, setCustomPaletteOpen] = useState(false);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setContact = (k, v) => setDraft((d) => ({ ...d, contacts: { ...d.contacts, [k]: v } }));

  const canNext = useMemo(() => {
    if (step === 1) return (draft.brand_name || "").trim().length > 0;
    if (step === 2) return !!draft.mood;
    if (step === 3) return !!draft.palette;
    if (step === 4) return !!draft.logo_shape;
    return true;
  }, [step, draft]);

  const submit = async (variant) => {
    setSaving(true);
    try {
      const payload = {
        brand_name: draft.brand_name.trim(),
        monogram: (draft.monogram || "").trim() || null,
        mood: draft.mood,
        palette: draft.palette,
        logo_shape: draft.logo_shape,
        contacts: {
          instagram: draft.contacts.instagram || null,
          phone: draft.contacts.phone || null,
          website: draft.contacts.website || null,
        },
        logo_variant: variant,
      };
      const bp = await saveBrandProfile(payload);
      onSaved(bp);
    } catch (e) { toast.error(formatApiError(e) || "Не удалось сохранить бренд"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.55)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="mo-brand-wizard">
      <div className="w-full md:max-w-2xl bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "94dvh" }}>

        <div className="flex-shrink-0 p-5 md:p-6 pb-2 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10.5px] tracking-[0.28em] uppercase mb-1" style={{ color: "#8B6F4B" }}>ANOKINA STUDIO</div>
            <div className="text-[19px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
              {isEdit ? "Редактировать бренд" : "Создать мой бренд"}
            </div>
          </div>
          <button onClick={onClose}
                  className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                  style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  aria-label="Закрыть"
                  data-testid="mo-brand-wizard-close">
            <X size={13} />
          </button>
        </div>

        <div className="flex-1 min-h-0 p-5 md:p-6 pt-2 overflow-y-auto">
          {step === 1 && (
            <WizardStep step={step} title="Как называется Ваш бренд?">
              <div className="space-y-3">
                <div>
                  <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Название бренда</div>
                  <input value={draft.brand_name}
                         onChange={(e) => setField("brand_name", e.target.value.slice(0, 80))}
                         placeholder="Например: Oksana Anokina"
                         className="w-full h-11 px-3 rounded-md bg-white text-base outline-none"
                         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                         data-testid="mo-brand-wizard-name" />
                </div>
                <div>
                  <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Инициалы / монограмма (необязательно)</div>
                  <input value={draft.monogram}
                         onChange={(e) => setField("monogram", e.target.value.slice(0, 6))}
                         placeholder="OA"
                         className="w-full h-11 px-3 rounded-md bg-white text-base outline-none tracking-[0.15em]"
                         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                         data-testid="mo-brand-wizard-monogram" />
                </div>
              </div>
            </WizardStep>
          )}

          {step === 2 && (
            <WizardStep step={step} title="Как должен ощущаться Ваш бренд?">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {MOODS.map((m) => (
                  <button key={m.id}
                          onClick={() => setField("mood", m.id)}
                          className="rounded-xl p-3.5 text-left transition"
                          style={{
                            background: draft.mood === m.id ? "#4A3728" : "#FFFFFF",
                            color: draft.mood === m.id ? "#F7EFE0" : "#2A1D14",
                            boxShadow: draft.mood === m.id
                              ? "0 6px 18px -12px rgba(74,55,40,0.55)"
                              : "inset 0 0 0 1px #EFE4D0",
                          }}
                          data-testid={`mo-brand-wizard-mood-${m.id}`}>
                    <div className="w-6 h-6 rounded-full mb-2" style={{ background: m.accent }} />
                    <div className="text-[13.5px]"
                         style={{ fontFamily: m.serifTitle ? "'Cormorant Garamond', serif" : "'Inter', sans-serif" }}>
                      {m.label}
                    </div>
                  </button>
                ))}
              </div>
            </WizardStep>
          )}

          {step === 3 && (
            <WizardStep step={step} title="Выберите настроение цвета">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {PALETTES.map((p) => (
                  <button key={p.id}
                          onClick={() => setField("palette", p)}
                          className="rounded-xl p-3 flex items-center gap-3 text-left transition"
                          style={{
                            background: draft.palette?.id === p.id ? "#4A3728" : "#FFFFFF",
                            color: draft.palette?.id === p.id ? "#F7EFE0" : "#2A1D14",
                            boxShadow: draft.palette?.id === p.id
                              ? "0 6px 18px -12px rgba(74,55,40,0.55)"
                              : "inset 0 0 0 1px #EFE4D0",
                          }}
                          data-testid={`mo-brand-wizard-palette-${p.id}`}>
                    <div className="flex gap-1 shrink-0">
                      {p.colors.map((c) => (
                        <span key={c} className="w-6 h-6 rounded-full"
                              style={{ background: c, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }} />
                      ))}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13.5px] leading-tight"
                           style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.name}</div>
                    </div>
                  </button>
                ))}
                <button onClick={() => setCustomPaletteOpen((v) => !v)}
                        className="rounded-xl p-3 text-left"
                        style={{ background: "#F7F1E7", color: "#4A3728",
                                 boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                        data-testid="mo-brand-wizard-custom-palette-toggle">
                  <div className="text-[13.5px]">Выбрать свои цвета</div>
                  <div className="text-[11px] mt-0.5" style={{ color: "#8B6F4B" }}>4 оттенка — от светлого к тёмному.</div>
                </button>
              </div>
              {customPaletteOpen && (
                <div className="mt-3 rounded-xl p-3 bg-white"
                     style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                  <div className="grid grid-cols-4 gap-2">
                    {(draft.palette?.colors || PALETTES[0].colors).map((color, i) => (
                      <label key={i} className="flex flex-col items-center gap-1">
                        <input type="color" value={color}
                               onChange={(e) => {
                                 const next = [...(draft.palette?.colors || PALETTES[0].colors)];
                                 next[i] = e.target.value;
                                 setField("palette", { id: "custom", name: "Свои цвета", colors: next });
                               }}
                               className="w-10 h-10 rounded-full border-0 cursor-pointer"
                               data-testid={`mo-brand-wizard-color-${i}`} />
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </WizardStep>
          )}

          {step === 4 && (
            <WizardStep step={step} title="Форма логотипа">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {LOGO_SHAPES.map((s) => (
                  <button key={s.id}
                          onClick={() => setField("logo_shape", s.id)}
                          className="rounded-xl p-3 text-center transition"
                          style={{
                            background: draft.logo_shape === s.id ? "#4A3728" : "#FFFFFF",
                            color: draft.logo_shape === s.id ? "#F7EFE0" : "#2A1D14",
                            boxShadow: draft.logo_shape === s.id
                              ? "0 6px 18px -12px rgba(74,55,40,0.55)"
                              : "inset 0 0 0 1px #EFE4D0",
                          }}
                          data-testid={`mo-brand-wizard-shape-${s.id}`}>
                    <div className="mx-auto mb-2 w-14 h-14 grid place-items-center"
                         style={{
                           background: draft.palette?.colors?.[0] || "#F7EFE0",
                           borderRadius: s.id === "round" ? "9999px" : s.id === "square" ? 4 : s.id === "horizontal" ? 4 : 4,
                           boxShadow: `inset 0 0 0 1.5px ${draft.palette?.colors?.[draft.palette.colors.length-1] || "#4A3728"}`,
                           width: s.id === "horizontal" ? 84 : 56,
                           height: s.id === "horizontal" ? 40 : 56,
                         }}>
                      <span style={{ fontFamily: "'Cormorant Garamond', serif",
                                     color: draft.palette?.colors?.[draft.palette.colors.length-1] || "#4A3728",
                                     fontSize: s.id === "horizontal" ? 18 : 22 }}>
                        {(draft.monogram || draft.brand_name || "A").slice(0, s.id === "horizontal" ? 4 : 2).toUpperCase()}
                      </span>
                    </div>
                    <div className="text-[12.5px]">{s.label}</div>
                  </button>
                ))}
              </div>
            </WizardStep>
          )}

          {step === 5 && (
            <WizardStep step={step} title="Как с Вами связаться? (необязательно)">
              <div className="space-y-3">
                <ContactRow icon="@" label="Instagram" value={draft.contacts.instagram}
                            onChange={(v) => setContact("instagram", v)}
                            placeholder="@your_brand"
                            testId="mo-brand-wizard-instagram" />
                <ContactRow icon="☎" label="Телефон" value={draft.contacts.phone}
                            onChange={(v) => setContact("phone", v)}
                            placeholder="+7 (000) 000-00-00"
                            testId="mo-brand-wizard-phone" />
                <ContactRow icon="🌐" label="Сайт" value={draft.contacts.website}
                            onChange={(v) => setContact("website", v)}
                            placeholder="anokina.studio"
                            testId="mo-brand-wizard-website" />
                <div className="text-[11px] mt-2" style={{ color: "#8B6F4B" }}>
                  QR-код и печатные материалы появятся в следующих обновлениях.
                </div>
              </div>
            </WizardStep>
          )}

          {step === 6 && (
            <WizardStep step={step} title="Выберите вариант логотипа">
              <div className="text-[12.5px] mb-3" style={{ color: "#5B4326" }}>
                4 направления одного бренда. Позже сюда подключится настоящая AI-генерация.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {LOGO_VARIANTS.map((v) => {
                  const isPicked = draft.logo_variant?.id === v.id;
                  return (
                    <div key={v.id}
                         className="rounded-xl p-3 flex flex-col gap-2"
                         style={{
                           background: "#FFFFFF",
                           boxShadow: isPicked
                             ? "inset 0 0 0 2px #4A3728"
                             : "inset 0 0 0 1px #EFE4D0",
                         }}
                         data-testid={`mo-brand-wizard-variant-${v.id}`}>
                      <div className="rounded-md overflow-hidden">
                        <LogoSVG variant={v.id} profile={draft} />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[12.5px]" style={{ color: "#5B4326" }}>{v.label}</div>
                        <button onClick={() => setField("logo_variant", v)}
                                className="h-8 px-3 rounded-full text-[12px] inline-flex items-center gap-1"
                                style={{
                                  background: isPicked ? "#4A3728" : "#F7F1E7",
                                  color: isPicked ? "#F7EFE0" : "#4A3728",
                                  boxShadow: isPicked ? "none" : "inset 0 0 0 1px #EFE4D0",
                                }}
                                data-testid={`mo-brand-wizard-variant-pick-${v.id}`}>
                          {isPicked ? (<><Check size={11} /> Выбрано</>) : "Выбрать этот вариант"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </WizardStep>
          )}
        </div>

        {/* Footer nav */}
        <div className="flex-shrink-0 p-5 md:p-6 pt-3 flex items-center justify-between border-t"
             style={{ borderColor: "#EFE4D0" }}>
          <button onClick={() => step > 1 ? setStep(step - 1) : onClose()}
                  className="h-10 px-3 rounded-full text-[12.5px] inline-flex items-center gap-1"
                  style={{ background: "transparent", color: "#4A3728",
                           boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  data-testid="mo-brand-wizard-prev">
            <ChevronLeft size={13} /> {step === 1 ? "Отмена" : "Назад"}
          </button>
          {step < 5 && (
            <button onClick={() => setStep(step + 1)} disabled={!canNext}
                    className="h-10 px-4 rounded-full text-[12.5px] inline-flex items-center gap-1 disabled:opacity-50"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-brand-wizard-next">
              Далее <ChevronRight size={13} />
            </button>
          )}
          {step === 5 && (
            <button onClick={() => setStep(6)}
                    className="h-10 px-4 rounded-full text-[12.5px] inline-flex items-center gap-1"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-brand-wizard-generate">
              Создать варианты <ChevronRight size={13} />
            </button>
          )}
          {step === 6 && (
            <button onClick={() => submit(draft.logo_variant)}
                    disabled={saving || !draft.logo_variant}
                    className="h-10 px-4 rounded-full text-[12.5px] inline-flex items-center gap-1 disabled:opacity-60"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-brand-wizard-save">
              {saving ? (<><Loader2 size={13} className="animate-spin" /> Сохраняем…</>)
                     : (isEdit ? "Сохранить изменения" : "Сохранить мой бренд")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactRow({ icon, label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1" style={{ color: "#8B6F4B" }}>{label}</div>
      <div className="flex items-stretch">
        <div className="w-11 grid place-items-center rounded-l-md text-[15px]"
             style={{ background: "#F7F1E7", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
          {icon}
        </div>
        <input value={value || ""} onChange={(e) => onChange(e.target.value.slice(0, 100))}
               placeholder={placeholder}
               className="flex-1 h-11 px-3 rounded-r-md bg-white text-base outline-none"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
               data-testid={testId} />
      </div>
    </div>
  );
}
